import authService from './auth.service';
import type {
  ServiceRegistry,
  ServiceRegistryListItem,
  ServiceCreate,
  ServiceUpdate,
  ServiceFieldCreate,
  ServiceFieldUpdate,
  EntityRecord,
  EntityListResponse,
  EntityCreate,
  EntityUpdate,
  EntityNavItem,
  MatrixResolveParams,
  MatrixResolveResult,
} from '../types/entity-engine.types';

const ADMIN = '/api/v1/entities/admin';
const DATA = '/api/v1/entities/data';

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.detail || `Ошибка ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

const entityEngineService = {
  // =========================================================================
  // Services (registry) — /api/v1/entities/admin/services
  // =========================================================================

  async getServices(): Promise<ServiceRegistryListItem[]> {
    const res = await authService.fetchWithAuth(`${ADMIN}/services`);
    return handleResponse(res);
  },

  // Deduplicate concurrent calls + short TTL cache
  _activeServicesCache: null as { data: EntityNavItem[]; ts: number } | null,
  _activeServicesInflight: null as Promise<EntityNavItem[]> | null,

  async getActiveServices(): Promise<EntityNavItem[]> {
    const now = Date.now();
    if (this._activeServicesCache && now - this._activeServicesCache.ts < 10_000) {
      return this._activeServicesCache.data;
    }
    if (this._activeServicesInflight) return this._activeServicesInflight;
    this._activeServicesInflight = (async () => {
      try {
        const res = await authService.fetchWithAuth(`${ADMIN}/services?active_only=true`);
        const items: ServiceRegistryListItem[] = await handleResponse(res);
        const mapped = items.map((s) => ({
          code: s.code,
          name: s.name,
          icon: s.icon,
          service_kind: s.service_kind || 'entity',
        }));
        this._activeServicesCache = { data: mapped, ts: Date.now() };
        return mapped;
      } finally {
        this._activeServicesInflight = null;
      }
    })();
    return this._activeServicesInflight;
  },

  clearCache() {
    this._activeServicesCache = null;
    this._activeServicesInflight = null;
  },

  async getService(code: string): Promise<ServiceRegistry> {
    const res = await authService.fetchWithAuth(`${ADMIN}/services/${code}`);
    return handleResponse(res);
  },

  async createService(data: ServiceCreate): Promise<ServiceRegistry> {
    const res = await authService.fetchWithAuth(`${ADMIN}/services`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  async updateService(code: string, data: ServiceUpdate): Promise<ServiceRegistry> {
    const res = await authService.fetchWithAuth(`${ADMIN}/services/${code}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  async deactivateService(code: string): Promise<void> {
    const res = await authService.fetchWithAuth(`${ADMIN}/services/${code}`, {
      method: 'DELETE',
    });
    await handleResponse<void>(res);
  },

  // =========================================================================
  // Fields — /api/v1/entities/admin/services/{code}/fields
  // =========================================================================

  async addField(serviceCode: string, data: ServiceFieldCreate): Promise<ServiceRegistry> {
    const res = await authService.fetchWithAuth(
      `${ADMIN}/services/${serviceCode}/fields`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );
    return handleResponse(res);
  },

  async updateField(
    serviceCode: string,
    fieldCode: string,
    data: ServiceFieldUpdate
  ): Promise<ServiceRegistry> {
    const res = await authService.fetchWithAuth(
      `${ADMIN}/services/${serviceCode}/fields/${fieldCode}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );
    return handleResponse(res);
  },

  async deactivateField(serviceCode: string, fieldCode: string): Promise<void> {
    const res = await authService.fetchWithAuth(
      `${ADMIN}/services/${serviceCode}/fields/${fieldCode}`,
      { method: 'DELETE' }
    );
    await handleResponse<void>(res);
  },

  // =========================================================================
  // Entity records (CRUD) — /api/v1/entities/data/{service_code}
  // =========================================================================

  async getEntities(
    serviceCode: string,
    params?: {
      filters?: Record<string, unknown>;
      sort_by?: string;
      sort_dir?: 'asc' | 'desc';
      offset?: number;
      limit?: number;
    }
  ): Promise<EntityListResponse> {
    const query = new URLSearchParams();
    if (params?.sort_by) query.set('sort_by', params.sort_by);
    if (params?.sort_dir) query.set('sort_dir', params.sort_dir);
    if (params?.offset !== undefined) query.set('offset', String(params.offset));
    if (params?.limit !== undefined) query.set('limit', String(params.limit));

    if (params?.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (value !== undefined && value !== null && value !== '') {
          query.set(key, String(value));
        }
      }
    }

    const qs = query.toString();
    const url = `${DATA}/${serviceCode}${qs ? `?${qs}` : ''}`;
    const res = await authService.fetchWithAuth(url);
    return handleResponse(res);
  },

  async searchEntities(
    serviceCode: string,
    params: {
      filters?: Record<string, unknown>;
      limit?: number;
      offset?: number;
      order_by?: string[];
      result_limit?: number;
    }
  ): Promise<EntityListResponse> {
    const res = await authService.fetchWithAuth(`${DATA}/${serviceCode}/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filters: params.filters || {},
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
        order_by: params.order_by,
        result_limit: params.result_limit,
      }),
    });
    return handleResponse(res);
  },

  async getEntity(serviceCode: string, entityId: string): Promise<EntityRecord> {
    const res = await authService.fetchWithAuth(`${DATA}/${serviceCode}/${entityId}`);
    return handleResponse(res);
  },

  async createEntity(serviceCode: string, data: EntityCreate, customerId?: number): Promise<EntityRecord> {
    const payload: { data: EntityCreate; customer_id?: number } = { data };
    if (customerId !== undefined) {
      payload.customer_id = customerId;
    }
    const res = await authService.fetchWithAuth(`${DATA}/${serviceCode}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleResponse(res);
  },

  async updateEntity(
    serviceCode: string,
    entityId: string,
    data: EntityUpdate
  ): Promise<EntityRecord> {
    const res = await authService.fetchWithAuth(`${DATA}/${serviceCode}/${entityId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data }),
    });
    return handleResponse(res);
  },

  async deleteEntity(serviceCode: string, entityId: string): Promise<void> {
    const res = await authService.fetchWithAuth(`${DATA}/${serviceCode}/${entityId}`, {
      method: 'DELETE',
    });
    await handleResponse<void>(res);
  },

  // =========================================================================
  // Matrix resolve — /api/v1/entities/data/{service_code}/resolve
  // =========================================================================

  async resolveMatrix(
    serviceCode: string,
    params: MatrixResolveParams
  ): Promise<MatrixResolveResult> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      query.set(key, String(value));
    }
    const res = await authService.fetchWithAuth(
      `${DATA}/${serviceCode}/resolve?${query.toString()}`
    );
    return handleResponse(res);
  },
};

export default entityEngineService;
